import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Calendar, MessageSquare, TrendingUp, TrendingDown, Eye, EyeOff, ExternalLink } from 'lucide-react';
import { Portfolio } from '../services/api';

interface PortfolioCardProps {
  portfolio: Portfolio;
}

const PortfolioCard: React.FC<PortfolioCardProps> = ({ portfolio }) => {
  const [showKeywords, setShowKeywords] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment.toLowerCase()) {
      case 'positive':
        return 'text-green-600 bg-green-100';
      case 'negative':
        return 'text-red-600 bg-red-100';
      case 'neutral':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getSentimentIcon = (sentiment: string) => {
    switch (sentiment.toLowerCase()) {
      case 'positive':
        return <TrendingUp className="h-4 w-4" />;
      case 'negative':
        return <TrendingDown className="h-4 w-4" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  const truncatedDescription = portfolio.description.length > 150 
    ? portfolio.description.substring(0, 150) + '...'
    : portfolio.description;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Briefcase className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 text-lg">Portfolio with ID: {portfolio.id}</h3>
              <div className="flex items-center space-x-2 mt-1">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-500">
                  {formatDate(portfolio.created_at)}
                </span>
              </div>
            </div>
          </div>
          
          {/* Sentiment Badge */}
          <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${getSentimentColor(portfolio.phrase_analysis.sentiment)}`}>
            {getSentimentIcon(portfolio.phrase_analysis.sentiment)}
            <span className="capitalize">{portfolio.phrase_analysis.sentiment}</span>
          </div>
        </div>

        {/* Description */}
        <div className="mb-4">
          <p className="text-gray-700 text-sm leading-relaxed">
            {showFullDescription ? portfolio.description : truncatedDescription}
          </p>
          {portfolio.description.length > 150 && (
            <button
              onClick={() => setShowFullDescription(!showFullDescription)}
              className="text-blue-600 text-sm font-medium mt-1 hover:text-blue-700"
            >
              {showFullDescription ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>

        {portfolio.domains_count > 0 && (
          <div className="mb-4">
            <p className="text-gray-700 text-sm leading-relaxed">
              {portfolio.domains_count} domains in this portfolio
            </p>
          </div>
        )}
        
        {/* Phrase Analysis */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <h4 className="font-medium text-gray-900 mb-2">Original Phrase</h4>
          <p className="text-gray-700 text-sm italic mb-3">"{portfolio.phrase_analysis.phrase}"</p>
          
          <div className="grid grid-cols-3 gap-4 text-xs">
            <div className="text-center">
              <div className="text-green-600 font-semibold">
                {(portfolio.phrase_analysis.pos * 100).toFixed(1)}%
              </div>
              <div className="text-gray-500">Positive</div>
            </div>
            <div className="text-center">
              <div className="text-red-600 font-semibold">
                {(portfolio.phrase_analysis.neg * 100).toFixed(1)}%
              </div>
              <div className="text-gray-500">Negative</div>
            </div>
            <div className="text-center">
              <div className="text-gray-600 font-semibold">
                {(portfolio.phrase_analysis.neu * 100).toFixed(1)}%
              </div>
              <div className="text-gray-500">Neutral</div>
            </div>
          </div>
        </div>

        {/* Keywords Section */}
        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-gray-900">
              Keywords ({portfolio.keywords_explosion.length})
            </h4>
            <button
              onClick={() => setShowKeywords(!showKeywords)}
              className="flex items-center space-x-1 text-blue-600 text-sm font-medium hover:text-blue-700"
            >
              {showKeywords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              <span>{showKeywords ? 'Hide' : 'Show'} keywords</span>
            </button>
          </div>
          
          {showKeywords && (
            <div className="max-h-40 overflow-y-auto">
              <div className="flex flex-wrap gap-2">
                {portfolio.keywords_explosion.map((keyword, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-4 flex space-x-2">
          <Link
            to={`/dashboard/portfolios/${portfolio.id}`}
            className="flex items-center space-x-1 text-blue-600 text-sm font-medium hover:text-blue-700"
          >
            <ExternalLink className="h-4 w-4" />
            <span>View Details</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PortfolioCard; 